import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(`/functions/v1/${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}

export interface SalaryRequest {
  id: string;
  tenant_id: string;
  employee_id: string;
  current_salary: number | null;
  requested_salary: number;
  currency: string;
  reason: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'withdrawn';
  reviewed_by: string | null;
  reviewed_at: string | null;
  reviewer_note: string | null;
  effective_date: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateSalaryRequestInput {
  employee_id: string;
  current_salary?: number | null;
  requested_salary: number;
  currency?: string;
  reason?: string | null;
  effective_date?: string | null;
}

export interface UpdateSalaryRequestInput {
  status?: SalaryRequest['status'];
  reviewer_note?: string | null;
  effective_date?: string | null;
}

export const salaryRequestKeys = {
  all: ['salary-requests'] as const,
  list: (tenantId: string) => [...salaryRequestKeys.all, 'list', tenantId] as const,
} as const;

export function useSalaryRequests(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: salaryRequestKeys.list(tenantId),
    queryFn: async () => {
      const { data, error } = await (supabase as unknown as { from: (t: string) => any })
        .from('salary_requests')
        // @ts-ignore dynamic
        .select('*')
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as SalaryRequest[];
    },
    enabled: Boolean(tenantId),
  });
}

export function useCreateSalaryRequest(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateSalaryRequestInput) => {
      const { data, error } = await (supabase as unknown as { from: (t: string) => any })
        .from('salary_requests')
        // @ts-ignore dynamic
        .insert({ ...input, tenant_id: tenantId })
        .select()
        .single();
      if (error) throw error;
      return data as SalaryRequest;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: salaryRequestKeys.list(tenantId) }),
  });
}

export function useUpdateSalaryRequest(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateSalaryRequestInput & { id: string }) => {
      const { data, error } = await (supabase as unknown as { from: (t: string) => any })
        .from('salary_requests')
        // @ts-ignore dynamic
        .update({ ...input, updated_at: new Date().toISOString() })
        .eq('id', id)
        .eq('tenant_id', tenantId)
        .select()
        .single();
      if (error) throw error;
      return data as SalaryRequest;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: salaryRequestKeys.list(tenantId) }),
  });
}

export function useDeleteSalaryRequest(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as unknown as { from: (t: string) => any })
        .from('salary_requests')
        // @ts-ignore dynamic
        .delete()
        .eq('id', id)
        .eq('tenant_id', tenantId);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: salaryRequestKeys.list(tenantId) }),
  });
}

export function useReviewSalaryRequest(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      decision,
      comment,
    }: { id: string; decision: 'approved' | 'rejected'; comment?: string }) =>
      apiFetch<SalaryRequest>(supabase, `addons/salary-requests/${id}/review`, {
        method: 'PATCH',
        body: JSON.stringify({ decision, comment }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: salaryRequestKeys.list(tenantId) }),
  });
}
